import { useState, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { initiateXamanLogin } from '../api/auth';

interface XamanData {
  uuid: string;
  qrCode: string;
  deepLink: string;
}

export type XamanMode = 'login' | 'link';

/**
 * Hook for managing Xaman login/link flow.
 * Can be used anywhere a login button is needed (sidebar, banner, blur overlay, etc.)
 * Supports both 'login' mode (for new authentication) and 'link' mode (for adding wallet to existing account)
 */
export function useXamanLogin() {
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState<XamanMode>('login');
  const [xamanData, setXamanData] = useState<XamanData>({
    uuid: '',
    qrCode: '',
    deepLink: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const { setLoading, setError } = useAuthStore();
  const { error: showError } = useNotificationStore();

  const initiateLogin = useCallback(async () => {
    setIsLoading(true);
    setLoading(true);
    setError(null);
    setMode('login');

    try {
      const data = await initiateXamanLogin();
      setXamanData({
        uuid: data.uuid,
        qrCode: data.qrCode,
        deepLink: data.deepLink,
      });
      setShowModal(true);
    } catch (err: any) {
      const message = err.message || 'Failed to initiate Xaman login';
      setError(message);
      showError(message);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  }, [setLoading, setError, showError]);

  /**
   * Initiate wallet linking flow (for adding another wallet to existing account)
   */
  const initiateLink = useCallback(async () => {
    setIsLoading(true);
    setLoading(true);
    setError(null);
    setMode('link');

    try {
      const data = await initiateXamanLogin();
      setXamanData({
        uuid: data.uuid,
        qrCode: data.qrCode,
        deepLink: data.deepLink,
      });
      setShowModal(true);
    } catch (err: any) {
      const message = err.message || 'Failed to initiate wallet linking';
      setError(message);
      showError(message);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  }, [setLoading, setError, showError]);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleSuccess = useCallback(() => {
    setShowModal(false);
  }, []);

  return {
    showModal,
    xamanData,
    isLoading,
    mode,
    initiateLogin,
    initiateLink,
    closeModal,
    handleSuccess,
  };
}
